/**
 * Boot-time recovery for stale-asset errors after a new deployment.
 *
 * Two classes of error share the same root cause — the service worker's
 * precache (or the browser's HTTP cache) contains chunks from an older build
 * while the page's index.html references assets from the new build:
 *
 *   JS module errors (TypeError):
 *     "TypeError: Importing a module script failed" (iOS Safari/Chrome)
 *     "Failed to fetch dynamically imported module" (desktop Chrome/Firefox)
 *     "error loading dynamically imported module"
 *
 *   CSS preload errors (plain Error):
 *     "Error: Unable to preload CSS for /App-De3mdMTO.css" (all browsers)
 *     Vite emits this when a `<link rel="preload">` for a content-hashed CSS
 *     chunk 404s because the file was replaced in the new deployment.
 *
 * The fix is two-step: (1) tell the waiting SW to skip its waiting phase so the
 * new SW activates immediately, bringing its fresh precache of the new asset
 * URLs; (2) reload.  Without the skip-waiting step the reloaded page still runs
 * the old SW, still gets a cache miss for the new chunks, and still needs the
 * network — so the same failure repeats on a spotty mobile connection.
 *
 * A sessionStorage flag prevents an infinite reload loop if the fresh page also
 * fails (e.g. deploy is still in progress or the new SW hasn't installed yet).
 */

export const BOOT_RELOAD_KEY = 'boot_module_import_reload';

/** Browser error messages that indicate a failed dynamic module import. */
const MODULE_IMPORT_ERROR_SUBSTRINGS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
] as const;

export function isModuleImportError(err: unknown): boolean {
  if (!(err instanceof TypeError)) return false;
  const msg = err.message ?? '';
  return MODULE_IMPORT_ERROR_SUBSTRINGS.some((s) => msg.includes(s));
}

/**
 * Detect Vite's CSS preload failure.  Vite throws a plain `Error` (not a
 * TypeError) with this prefix when a `<link rel="preload">` for a
 * content-hashed CSS chunk responds with a non-200 status — the same stale-
 * deployment root cause as JS module import failures.
 */
export function isCssPreloadError(err: unknown): boolean {
  // Enforce the "plain Error, not TypeError" contract above so this stays
  // mutually exclusive with isModuleImportError (which requires a TypeError).
  if (!(err instanceof Error) || err instanceof TypeError) return false;
  return err.message.startsWith('Unable to preload CSS for');
}

/**
 * Tell the waiting service worker (if any) to skip its waiting phase and
 * activate immediately.  This is best-effort: if the SW API is unavailable or
 * the message fails for any reason, the reload still proceeds.
 *
 * On mobile the new SW may still be in the `installing` state when a boot
 * failure triggers this function.  If we only check `reg.waiting` and it is
 * null, the SKIP_WAITING message is never sent, the old SW stays active
 * through the reload, and the stale-precache failure repeats — the second
 * failure lands in Sentry because `BOOT_RELOAD_KEY` is already set.  So we
 * also check `reg.installing` and wait up to 3 s for it to reach the
 * `installed`/waiting state before sending SKIP_WAITING. (GlitchTip #3318)
 *
 * Exported for unit testing only.
 */
export async function skipWaitingServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/');
    if (!reg) return;

    if (reg.waiting) {
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      return;
    }

    // New SW still installing — wait for it to reach the waiting state so
    // SKIP_WAITING is actually delivered before the reload.
    const installing = reg.installing;
    if (!installing) return;

    await new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, 3000);
      const onStateChange = () => {
        if (installing.state !== 'installing') {
          clearTimeout(timer);
          installing.removeEventListener('statechange', onStateChange);
          if (installing.state === 'installed') {
            installing.postMessage({ type: 'SKIP_WAITING' });
          }
          resolve();
        }
      };
      installing.addEventListener('statechange', onStateChange);
      // Fire once immediately in case the state already advanced past
      // 'installing' before the listener was registered.
      onStateChange();
    });
  } catch {
    // Best-effort — proceed with reload regardless
  }
}

/**
 * Handle a boot-phase failure by reloading once on module import errors, then
 * delegating to the caller for persistent failures or other error types.
 *
 * Before reloading, sends SKIP_WAITING to the pending service worker so the
 * new SW's precache (containing the updated chunk URLs) is active by the time
 * the reloaded page runs its dynamic imports.
 *
 * @param err              - The caught error.
 * @param onReload         - Called when a reload is attempted (e.g. window.location.reload).
 * @param onPersistentFail - Called when the error should be reported (e.g. Sentry.captureException).
 */
export async function attemptBootRecovery(
  err: unknown,
  onReload: () => void,
  onPersistentFail: (err: unknown) => void,
): Promise<void> {
  if (isModuleImportError(err) || isCssPreloadError(err)) {
    try {
      if (!sessionStorage.getItem(BOOT_RELOAD_KEY)) {
        sessionStorage.setItem(BOOT_RELOAD_KEY, '1');
        // Activate the new SW first so its fresh precache serves the new chunk
        // URLs on the reloaded page — without this the old SW's stale precache
        // causes the same module-import failure again.
        await skipWaitingServiceWorker();
        onReload();
        return;
      }
    } catch {
      // sessionStorage unavailable (private mode, quota exceeded): fall through
      // to persistent-failure path — reloading blindly could loop forever.
    }
  }
  onPersistentFail(err);
}

/** Say why the app couldn't start instead of a blank #root (ADR-0007, #7524). Plain DOM: React may be what failed. */
export function showBootFailure(root: HTMLElement | null, online: boolean): void {
  if (!root) return;
  const box = document.createElement('div');
  box.setAttribute('role', 'alert');
  box.style.cssText = 'max-width:28rem;margin:20vh auto 0;padding:0 16px;font-family:system-ui,sans-serif;text-align:center;line-height:1.5';
  const message = document.createElement('p');
  message.textContent = online
    ? "The app couldn't start. Reload to try again. If it keeps happening, tell your manager."
    : 'Connect to the internet once to finish setting up the app. After that it opens without a signal.';
  const reload = document.createElement('button');
  reload.type = 'button';
  reload.textContent = 'Reload';
  reload.style.cssText = 'margin-top:12px;padding:8px 20px;font:inherit';
  reload.addEventListener('click', () => window.location.reload());
  box.append(message, reload);
  root.replaceChildren(box);
}
